import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { APP_URL, DEFAULT_META_DATA } from 'domain/constants';

interface Props {
  title?: string;
  description?: string;
  imageUrl?: string;
  noindex?: boolean;
}

const truncate = (text: string, limit = 160) =>
  text.length > limit ? `${text.slice(0, limit - 1).trimEnd()}…` : text;

const MetaTags: React.FC<Props> = ({ title, description, imageUrl, noindex }): JSX.Element => {
  const { asPath } = useRouter();

  const fullTitle = !title
    ? DEFAULT_META_DATA.title
    : title.includes(DEFAULT_META_DATA.title)
    ? title
    : `${title} | ${DEFAULT_META_DATA.title}`;
  const metaDescription = truncate(description || DEFAULT_META_DATA.description);
  const metaImage = imageUrl || DEFAULT_META_DATA.imageUrl;
  const canonicalUrl = `${APP_URL}${asPath.split(/[?#]/)[0]}`.replace(/\/$/, '') || APP_URL;

  return (
    <Head>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      {noindex ? (
        <meta name="robots" content="noindex,nofollow" />
      ) : (
        <link rel="canonical" href={canonicalUrl} />
      )}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content="website" />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={DEFAULT_META_DATA.title} />
      <meta property="og:locale" content="uk_UA" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />
    </Head>
  );
};

export default MetaTags;
